import {
  Alert,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Button,
  Group,
  Menu,
  Modal,
  NavLink,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  hasRole,
  useAppVersion,
  useCurrentUser,
  useIstTestsystem,
  useLogout,
} from "../hooks/useAuth.js";
import { useAufgaben } from "../hooks/useAufgaben.js";
import { useCustomerViewConnection } from "../hooks/useCustomerView.js";
import { useSetOwnName } from "../hooks/useUsers.js";
import { baueNavigation } from "./navigation.js";
import { ColorSchemeSwitcher } from "./ColorSchemeSwitcher.js";
import { FeedbackButton } from "./FeedbackButton.js";
import { LanguageSwitcher } from "./LanguageSwitcher.js";
import { TestsystemBanner } from "./TestsystemBanner.js";
import { ViewAsBanner } from "./ViewAsBanner.js";
import { ApiTokensCard } from "./profile/ApiTokensCard.js";
import { BetriebPanel } from "./settings/BetriebPanel.js";
import { ViewAsHistoryModal } from "./ViewAsHistoryModal.js";
import { ViewAsPicker } from "./ViewAsPicker.js";
import logoDunkelUrl from "../assets/mindsquare-logo-dunkel.svg";
import logoUrl from "../assets/mindsquare-logo.svg";

interface AppLayoutProps {
  children: ReactNode;
}

/** Merkt sich nur, ob die Navigation am Desktop eingeklappt ist — mobil
 * startet die Navigation immer eingeklappt (Overlay), das braucht keinen
 * eigenen Speicher. */
export const NAV_COLLAPSED_KEY = "msc.nav.collapsed";

function readNavCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    // Privater Modus oder Policy kann localStorage sperren — dann bleibt es
    // beim Standard (ausgeklappt).
    return false;
  }
}

function writeNavCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Siehe readNavCollapsed — die Einstellung gilt dann nur für diese
    // Sitzung.
  }
}

export function AppLayout({ children }: AppLayoutProps) {
  const theme = useMantineTheme();
  // Ein Burger fuer beide Breakpoints (statt zwei getrennten): mobil oeffnet
  // er die Navigation als Overlay, am Desktop klappt er sie ein/aus. Welche
  // der beiden Zustaende der Klick trifft, entscheidet die aktuelle
  // Fensterbreite — Mantine blendet ohnehin nur den passenden Navbar-Zustand
  // ein (navbar.collapsed.mobile / .desktop je nach Breakpoint).
  const isMobileViewport = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) ?? false;
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
    useDisclosure(false);
  const [desktopOpened, setDesktopOpened] = useState(() => !readNavCollapsed());
  const [profileOpen, profileHandlers] = useDisclosure(false);
  const [tokensOpen, tokensHandlers] = useDisclosure(false);
  const [betriebOpen, betriebHandlers] = useDisclosure(false);
  const [viewAsOpen, viewAsHandlers] = useDisclosure(false);
  const [viewAsHistoryOpen, viewAsHistoryHandlers] = useDisclosure(false);
  const { user } = useCurrentUser();
  const logout = useLogout();
  const version = useAppVersion();
  const istTestsystem = useIstTestsystem();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { t } = useTranslation();

  // Mobil schliesst ein Klick auf einen Navigationspunkt das Overlay wieder —
  // sonst verdeckt es die Seite, auf die man gerade gewechselt ist. Der
  // Effekt betrifft nur `mobileOpened`; der Desktop-Zustand bleibt unberuehrt.
  useEffect(() => {
    closeMobile();
  }, [currentPath, closeMobile]);

  function toggleNav() {
    if (isMobileViewport) {
      toggleMobile();
      return;
    }
    setDesktopOpened((prev) => {
      const next = !prev;
      writeNavCollapsed(!next);
      return next;
    });
  }

  const isAdmin = hasRole(user, "admin");
  // Wer ueberhaupt eine fremde Sicht uebernehmen kann. Die echte Grenze zieht
  // der Server (viewAs.service.ts) — das hier blendet nur einen Menuepunkt aus,
  // der sonst fuer Projektleiter ins Leere fuehrte.
  const canAssumeViews = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);
  // BCG-Daten sind sensitiv (Umsatz, Wachstum). Nur Rollen mit fachlichem
  // Bezug bekommen den Link zu sehen — siehe canViewSegmentBcg im Backend.
  const canSeeBcg = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
  ]);
  // Freigaben-Inbox und Einstellungen liefern nur für diese Rollen Inhalte
  // (inboxForUser bzw. Schreib-Gates) — für alle anderen wären es tote Türen.
  const canManage = hasRole(user, ["admin", "bereichsleiter", "stellvertreter"]);
  // Datenlücken: dieselben Rollen wie /dashboard/hinweise und RequireRole in router.ts.
  const canSeeDatenluecken = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);
  // Service Leads (implizite Rolle `projektleiter`) bekommen ihren eigenen
  // Portfolio-Einstieg, statt ihn im Bereichs-Cockpit suchen zu müssen.
  const isServiceLead = hasRole(user, "projektleiter");
  // Wartungsplanung: Leseansichten sind für dieselben fünf Standardrollen
  // freigegeben wie die Seite selbst (RequireRole in router.ts, Spec §6).
  const canSeeMaintenance = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
  ]);
  // Customer View erscheint nur, wenn der Server sie konfiguriert meldet —
  // sonst führte der Eintrag auf eine Seite, die 503 antwortet.
  const customerView = useCustomerViewConnection();
  const canSeeCustomerView = customerView.data?.configured === true;
  // Badge an „Aufgaben": wie viel überfällig ist. Dieselbe Abfrage wie die
  // Startseite, also kein zweiter Abruf.
  const aufgaben = useAufgaben();
  const ueberfaellig =
    aufgaben.data?.items.filter((i) => i.dringlichkeit === "ueberfaellig").length ?? 0;

  function isActive(to: string, exact = false) {
    if (exact) return currentPath === to;
    return currentPath === to || currentPath.startsWith(`${to}/`);
  }

  const nav = baueNavigation({
    canSeeBcg,
    canManage,
    canSeeDatenluecken,
    isServiceLead,
    canSeeMaintenance,
    canSeeCustomerView,
  });

  return (
    <AppShell
      // Das Warnband sitzt IM Kopfbereich, nicht im Inhalt: Der Kopf ist fest
      // verankert und bleibt beim Scrollen stehen. Ein Band, das nach der
      // ersten Bildschirmseite verschwindet, hilft genau dann nicht, wenn man
      // tief in einer Maske sitzt und vergessen hat, wo man ist. Der Preis ist
      // diese abhängige Höhe — 60 wie bisher, 86 mit Band.
      header={{ height: istTestsystem ? 86 : 60 }}
      navbar={{
        width: 240,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <TestsystemBanner />
        <Group h={60} px="md" justify="space-between">
          <Group>
            <Burger
              opened={isMobileViewport ? mobileOpened : desktopOpened}
              onClick={toggleNav}
              aria-label={t("nav.toggle")}
              size="sm"
            />
            {/* Die Wortmarke ist dunkelgrau — auf dunklem Grund steht die
                helle Fassung (#337). */}
            <Box
              component="img"
              src={logoUrl}
              alt="mindsquare"
              darkHidden
              h={26}
            />
            <Box
              component="img"
              src={logoDunkelUrl}
              alt="mindsquare"
              lightHidden
              h={26}
            />
            <Text fw={500} size="sm" c="dimmed" visibleFrom="sm">
              {t("app.productName")}
            </Text>
          </Group>
          <Group>
            {/* Der Feedback-Knopf steht vor der Sprachwahl: Er gehört zu dem,
                was man gerade tut, nicht zu den Einstellungen. Er blendet sich
                selbst aus, wenn der Server kein Forgejo-Token hat. */}
            <FeedbackButton />
            <LanguageSwitcher />
            {user && (
              <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton>
                    <Group gap="sm" align="center">
                      <Avatar size="sm" color="blue">
                        {user.fullName
                          .split(" ")
                          .map((n) => n[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()}
                      </Avatar>
                      <Text size="sm">{user.fullName}</Text>
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>
                    {user.email}
                    {version ? (
                      <Text size="xs" c="dimmed">
                        {t("app.version", { version })}
                      </Text>
                    ) : null}
                  </Menu.Label>
                  <Menu.Item onClick={profileHandlers.open}>
                    {t("auth.editProfile")}
                  </Menu.Item>
                  <Menu.Item onClick={tokensHandlers.open}>
                    {t("apiTokens.menuItem")}
                  </Menu.Item>
                  <Menu.Item component={Link} to="/benachrichtigungen">
                    {t("auth.notifications")}
                  </Menu.Item>
                  {/* Für alle: Wer mit dem CRM arbeitet, soll sehen können, ob
                      die nächtlichen Abgleiche gelaufen sind. Nur der Status —
                      die Logs bleiben im Einstellungs-Reiter für Admins. */}
                  <Menu.Item onClick={betriebHandlers.open}>
                    {t("pages.settings.betrieb.menuItem")}
                  </Menu.Item>
                  {canAssumeViews && !user.assumedBy ? (
                    <Menu.Item onClick={viewAsHandlers.open}>
                      {t("viewAs.menuItem")}
                    </Menu.Item>
                  ) : null}
                  {/* Bewusst ohne Rollenbedingung: Wer betroffen sein kann,
                      muss auch nachsehen koennen. */}
                  <Menu.Item onClick={viewAsHistoryHandlers.open}>
                    {t("viewAs.historyTitle")}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Label>{t("colorScheme.label")}</Menu.Label>
                  <Box px="xs" pb="xs">
                    <ColorSchemeSwitcher />
                  </Box>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    onClick={() => logout.mutate()}
                    disabled={logout.isPending}
                  >
                    {t("auth.logout")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      {/* data-collapsed ist ein reiner Test-Haken: Mantine steuert das
          Ein-/Ausklappen selbst über eingeblendetes CSS (AppShellMediaStyles),
          nicht über ein Attribut am Element — ohne diesen Haken ließe sich
          der Zustand aus dem DOM heraus nicht zusichern. */}
      <AppShell.Navbar
        p="xs"
        data-collapsed={String(isMobileViewport ? !mobileOpened : !desktopOpened)}
      >
        <NavLink
          component={Link}
          to={nav.start.to}
          label={t(nav.start.labelKey)}
          leftSection={<nav.start.icon size={18} />}
          rightSection={
            ueberfaellig > 0 ? (
              <Badge size="sm" color="orange" variant="filled" data-testid="aufgaben-ueberfaellig">
                {ueberfaellig}
              </Badge>
            ) : null
          }
          variant="light"
          active={isActive(nav.start.to, nav.start.exact)}
        />
        {nav.gruppen.map((gruppe) => (
          <div key={gruppe.key}>
            <Text
              size="xs"
              fw={700}
              c="dimmed"
              tt="uppercase"
              mt="md"
              mb={4}
              px="sm"
            >
              {t(gruppe.labelKey)}
            </Text>
            {gruppe.eintraege.map((item) => (
              <div key={item.to}>
                <NavLink
                  component={Link}
                  to={item.to}
                  label={t(item.labelKey)}
                  leftSection={<item.icon size={18} />}
                  variant="light"
                  active={isActive(item.to, item.exact)}
                />
                {item.to === "/settings" && isAdmin && (
                  <NavLink
                    component={Link}
                    to="/settings/users"
                    label={t("nav.settingsUsers")}
                    pl="lg"
                    variant="light"
                    active={isActive("/settings/users", true)}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <ViewAsBanner />
        {children}
      </AppShell.Main>

      <OwnProfileModal opened={profileOpen} onClose={profileHandlers.close} />
      <Modal
        opened={tokensOpen}
        onClose={tokensHandlers.close}
        title={t("apiTokens.modalTitle")}
        size="xl"
      >
        <ApiTokensCard />
      </Modal>
      <Modal
        opened={betriebOpen}
        onClose={betriebHandlers.close}
        title={t("pages.settings.betrieb.menuItem")}
        size="xl"
      >
        <BetriebPanel admin={false} />
      </Modal>
      <ViewAsPicker opened={viewAsOpen} onClose={viewAsHandlers.close} />
      <ViewAsHistoryModal
        opened={viewAsHistoryOpen}
        onClose={viewAsHistoryHandlers.close}
      />
    </AppShell>
  );
}

function OwnProfileModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { user } = useCurrentUser();
  const setName = useSetOwnName();
  const [name, setName_] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (opened) {
      setName_(user?.fullName ?? "");
      setError(null);
    }
  }, [opened, user?.fullName]);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("pages.profile.nameError"));
      return;
    }
    if (trimmed === user?.fullName) {
      handleClose();
      return;
    }
    try {
      await setName.mutateAsync({ fullName: trimmed });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.profile.saveError"));
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t("pages.profile.title")} size="sm" centered>
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          {user?.email}
        </Text>
        <TextInput
          label={t("pages.profile.nameLabel")}
          value={name}
          onChange={(e) => setName_(e.currentTarget.value)}
          autoFocus
          required
        />
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={setName.isPending}>
            {t("common.cancel")}
          </Button>
          <Button loading={setName.isPending} onClick={() => void handleSubmit()}>
            {t("pages.profile.saveButton")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
